// auth.js
// Firebase authentication API functions

import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { generateMembershipID } from '../utils/generateMembershipID';

/**
 * Get next membership ID number
 * @returns {Promise<number>} Next membership number
 */
export const getNextMembershipNumber = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('membershipID', '!=', ''));
    const snapshot = await getDocs(q);
    
    // Extract all existing membership numbers
    const membershipNumbers = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data.membershipID) {
        // Extract number from format like "KOS001"
        const match = data.membershipID.match(/\d+$/);
        if (match) {
          membershipNumbers.push(parseInt(match[0]));
        }
      }
    });
    
    // Get the highest number and add 1
    const maxNumber = membershipNumbers.length > 0 ? Math.max(...membershipNumbers) : 0;
    return maxNumber + 1;
  } catch (error) {
    console.error('Error getting next membership number:', error);
    // Fallback to timestamp-based number if error occurs
    return Date.now() % 10000;
  }
};

/**
 * Login user with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} User credential object
 */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const userRef = doc(db, 'users', user.uid);
    let userDoc = null;
    let userData = null;
    
    // Keep Auth login independent from Firestore profile writes.
    // If Firestore rules block updating/creating the profile doc, we still want login to succeed.
    try {
      userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create a minimal profile doc so the rest of the app can function.
        // Firestore rules enforce that role must be 'member' for self-created docs.
        await setDoc(userRef, {
          email: user.email || email,
          role: 'member',
          points: 0,
          isActive: true,
          emailVerified: !!user.emailVerified,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLogin: serverTimestamp()
        });
      } else {
        // Use merge-write so login doesn't fail if legacy docs are missing fields.
        await setDoc(userRef, {
          lastLogin: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      userDoc = await getDoc(userRef);
      userData = userDoc.exists() ? userDoc.data() : null;
    } catch (firestoreError) {
      console.warn('Login: Firestore user profile read/write failed; continuing Auth login.', firestoreError);
      try {
        userDoc = await getDoc(userRef);
        userData = userDoc.exists() ? userDoc.data() : null;
      } catch {
        userData = null;
      }
    }
    
    return {
      user: user,
      userData: userData
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Register new user
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @param {Object} userData - Additional user data (title, firstName, lastName, phone, workplace, etc.)
 * @returns {Promise<Object>} User credential object
 */
export const registerUser = async (email, password, userData) => {
  try {
    // Create user account
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update user profile with display name
    if (userData.name) {
      await updateProfile(user, {
        displayName: userData.name
      });
    }

    // Send email verification
    await sendEmailVerification(user);

    // Generate membership ID if not provided
    let membershipID = userData.membershipID;
    if (!membershipID) {
      const nextNumber = await getNextMembershipNumber();
      membershipID = generateMembershipID('KOS', nextNumber);
    }

    // Prepare complete user data for Firestore
    const completeUserData = {
      // Basic Info
      email: email,
      title: userData.title || '',
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      name: userData.name || '',
      phone: userData.phone || '',
      
      // Professional Info
      workplace: userData.workplace || '',
      designation: userData.designation || '',
      specialty: userData.specialty || '',
      country: userData.country || '',
      
      // Membership Info
      membershipID: membershipID,
      role: userData.role || 'member',
      points: 0,
      
      // Status
      emailVerified: false,
      isActive: true,
      
      // Timestamps
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      // Any additional data
      ...userData
    };

    // Store user data in Firestore with Auth UID as document ID
    await setDoc(doc(db, 'users', user.uid), completeUserData);

    return {
      user: user,
      userData: completeUserData
    };
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Logout current user
 * @returns {Promise<void>}
 */
export const logoutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Check if email already exists in Firebase Auth
 * @param {string} email - User's email address
 * @returns {Promise<boolean>} True if email exists, false otherwise
 */
export const checkEmailExists = async (email) => {
  try {
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);
    return signInMethods.length > 0;
  } catch (error) {
    console.error('Check email exists error:', error);
    throw error;
  }
};

/**
 * Reset password
 * @param {string} email - User's email address
 * @returns {Promise<void>}
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

/**
 * Get current user data from Firestore
 * @param {string} uid - User ID
 * @returns {Promise<Object>} User data object
 */
export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    
    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      throw new Error('User data not found');
    }
  } catch (error) {
    console.error('Get user data error:', error);
    throw error;
  }
};

/**
 * Update user profile
 * @param {string} uid - User ID
 * @param {Object} updates - Profile updates
 * @returns {Promise<void>}
 */
export const updateUserProfile = async (uid, updates) => {
  try {
    await setDoc(doc(db, 'users', uid), {
      ...updates,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    console.error('Update profile error:', error);
    throw error;
  }
};
